"use client";
// Imports
import {
  getFirstImage,
  getShowAddToMyShow,
  getDatesEventIsOn,
  getEventTimesByDay,
  getPrice,
} from "@/lib/helpers";
import { Card as ShadCard } from "../../ui/card";
import {
  AddToMyShowButton,
  ViewDetails,
  Placeholder,
  Img,
  NewsPublishInfo,
  EventTimes,
} from "@/components";
import Link from "next/link";
import {
  COMPETITION_UI,
  isLateEntriesPeriod,
  isLateEntriesTagTitle,
  isEntriesClosedTagTitle,
  shouldShowEntriesClosedTag,
} from "@/lib/competitionEntries";
import { useEffect } from "react";

// Card
export function Card({
  userId,
  collectionHandle,
  data,
  selectedDay,
  showDates,
  setLastGridItemIsAd,
  mapIsActive,
  instanceId,
  priority = false,
  index = 0,
}) {
  useEffect(() => {
    if (setLastGridItemIsAd) {
      setLastGridItemIsAd(false);
    }
  }, []);

  // Variables
  const { title } = data;
  const image = getFirstImage(collectionHandle, data);
  const showAddToMyShow = getShowAddToMyShow(collectionHandle);

  let eventTimes;

  if (collectionHandle == "events") {
    const dates = getDatesEventIsOn(data, showDates);
    eventTimes = getEventTimesByDay(dates, selectedDay);
  }

  // CSS enter animation — Framer initial opacity:0 can stick under React 19
  const titleClassName = "mb-2 level-3 min-w-0 max-w-full break-words";

  return (
    <div
      className="flex flex-col w-full min-w-0 animate-card-in"
      style={{
        "--anim-delay": `${Math.min(index, 15) * 25}ms`,
        contentVisibility: "auto",
        containIntrinsicSize: "auto 520px",
      }}
      data-instance-id={instanceId}
      data-event-slug={data.slug}
    >
        <ShadCard
          className={`flex flex-col h-full min-w-0 overflow-hidden ${
            collectionHandle === "competitions" ||
            collectionHandle === "events" ||
            collectionHandle === "rides" ||
            collectionHandle === "commercial_exhibitors" ||
            collectionHandle === "showbags" ||
            collectionHandle === "food_outlets"
              ? "bg-white"
              : ""
          }`}
        >
          <>
            {data.show_as_page || data.link_to_external_site ? (
              <Link
                href={data.external_link ? data.external_link : data.url}
                target={data.external_link ? "_blank" : "_self"}
              >
                <CardImage
                  userId={userId}
                  image={image}
                  showAddToMyShow={showAddToMyShow}
                  collectionHandle={collectionHandle}
                  data={data}
                  priority={priority}
                />
              </Link>
            ) : (
              <CardImage
                image={image}
                userId={userId}
                showAddToMyShow={showAddToMyShow}
                collectionHandle={collectionHandle}
                data={data}
                priority={priority}
              />
            )}
          </>
          <div className="flex flex-col flex-1 justify-between px-2 pb-2 space-y-4 min-w-0 lg:px-4 lg:pb-4">
            <div className="min-w-0">
              {data.show_as_page || data.link_to_external_site ? (
                <Link
                  href={data.external_link ? data.external_link : data.url}
                  target={data.external_link ? "_blank" : "_self"}
                  className="block min-w-0 max-w-full"
                >
                  <h2 className={titleClassName}>{title}</h2>
                </Link>
              ) : (
                <h2 className={titleClassName}>{title}</h2>
              )}
              {collectionHandle == "news" && (
                <NewsPublishInfo data={data} display="grid" />
              )}
            </div>{" "}
            <div className="min-w-0 space-y-4">
              <EventTimes
                collectionHandle={collectionHandle}
                everyDay={data?.every_day}
                eventTimes={eventTimes}
                selectedDay={selectedDay}
                hideTimesOnCard={data?.hide_times_on_card}
              />
              <div className="flex gap-2 items-center w-full">
                <div className="min-w-0 flex-1">
                  <ViewDetails
                    collectionHandle={collectionHandle}
                    data={data}
                    image={image}
                    showAddToMyShow={showAddToMyShow}
                    eventTimes={eventTimes}
                    mapIsActive={mapIsActive}
                    instanceId={instanceId}
                  />
                </div>
                {showAddToMyShow && (
                  <div className="flex shrink-0 justify-end">
                    <AddToMyShowButton
                      userId={userId}
                      type={collectionHandle}
                      slug={data.slug}
                      image={image}
                      title={title}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </ShadCard>
    </div>
  );
}

export function CardImage({
  userId,
  image,
  showAddToMyShow,
  collectionHandle,
  data,
  priority = false,
}) {
  const price =
    collectionHandle === "showbags" ? getPrice(collectionHandle, data) : null;
  const tags = (() => {
    if (collectionHandle !== "competitions") return data?.tags ?? [];
    const cmsTags = data?.tags ?? [];
    if (
      isLateEntriesPeriod(data?.entries_close, data?.late_entries_close) &&
      !cmsTags.some((t) => isLateEntriesTagTitle(t?.title))
    ) {
      return [{ title: COMPETITION_UI.LATE_ENTRIES_LABEL }, ...cmsTags];
    }
    if (
      shouldShowEntriesClosedTag(
        data?.entries_open,
        data?.entries_close,
        data?.late_entries_close
      ) &&
      !cmsTags.some((t) => isEntriesClosedTagTitle(t?.title))
    ) {
      return [{ title: COMPETITION_UI.ENTRIES_CLOSED_LABEL }, ...cmsTags];
    }
    return cmsTags;
  })();

  if (!image?.permalink) {
    return (
      <div className="p-2 max-w-full lg:p-4 aspect-square">
        <div className="flex overflow-hidden relative justify-center items-center border-2 aspect-square rounded-card-radius">
          <Placeholder className="aspect-square" type="image-placeholder" />
          {tags.length > 0 && (
            <div className="flex absolute top-2 right-2 z-10 flex-col gap-2">
              <div className="flex flex-wrap gap-1 lg:gap-2 justify-end max-w-[150px] lg:max-w-[200px]">
                {tags.slice(0, 2).map((tag, tagIndex) => (
                  <p
                    key={tagIndex}
                    className="px-2 py-1 text-xs font-bold rounded-full border border-brand-fawn bg-white text-brand-fawn shadow-md backdrop-blur-md lg:px-4 lg:py-2 lg:text-sm"
                  >
                    {tag?.title}
                  </p>
                ))}
              </div>
            </div>
          )}
          {price && (
            <PriceBadge price={price} />
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="p-2 max-w-full lg:p-4 aspect-square">
      <div className="relative aspect-square">
        <Img
          className="w-full rounded-lg"
          sizes="(max-width: 1024px) 50vw, 400px"
          fill
          data={image}
          priority={priority}
        />
        {tags.length > 0 && (
          <div className="flex absolute top-2 right-2 z-10 flex-col gap-2">
            <div className="flex flex-wrap gap-1 lg:gap-2 justify-end max-w-[150px] lg:max-w-[200px]">
              {tags.slice(0, 2).map((tag, tagIndex) => (
                <p
                  key={tagIndex}
                  className="px-2 py-1 text-xs font-bold rounded-full border border-brand-fawn bg-white text-brand-fawn shadow-md backdrop-blur-md lg:px-4 lg:py-2 lg:text-sm"
                >
                  {tag?.title}
                </p>
              ))}
            </div>
          </div>
        )}
        {price && (
          <PriceBadge price={price} />
        )}
      </div>
    </div>
  );
}

function PriceBadge({ price }) {
  const scallops = [
    [85, 50],
    [82.34, 63.39],
    [74.75, 74.75],
    [63.39, 82.34],
    [50, 85],
    [36.61, 82.34],
    [25.25, 74.75],
    [17.66, 63.39],
    [15, 50],
    [17.66, 36.61],
    [25.25, 25.25],
    [36.61, 17.66],
    [50, 15],
    [63.39, 17.66],
    [74.75, 25.25],
    [82.34, 36.61],
  ];

  return (
    <p className="absolute top-3 right-3 z-10 flex h-14 w-14 items-center justify-center leading-none lg:top-4 lg:right-4 lg:h-[4.5rem] lg:w-[4.5rem]">
      <svg
        aria-hidden="true"
        className="absolute inset-0 h-full w-full text-brand-orange drop-shadow-md"
        viewBox="0 0 100 100"
      >
        <circle cx="50" cy="50" r="35" fill="currentColor" />
        {scallops.map(([cx, cy]) => (
          <circle
            key={`${cx}-${cy}`}
            cx={cx}
            cy={cy}
            r="10.5"
            fill="currentColor"
          />
        ))}
      </svg>
      <span className="relative z-10 whitespace-nowrap text-base font-black tracking-tight text-brand-wool lg:text-xl">
        {price}
      </span>
    </p>
  );
}
